import { defineStore } from "pinia";
import { ref, watch } from "vue";

import {
  fetchPulse as fetchPulseReq,
  fetchPulseNew as fetchPulseNewReq,
} from "@/api/repositories/discovery.js";

import { onlineProfiles as onlineProfilesReq } from "@/api/repositories/discovery.js";

import {
  PULSE_CACHE_DURATION,
  APP_VERSION,
  MIN_PULSE_ENTRIES_BEFORE_INJECTION,
  MAX_PULSE_ENTRIES_BEFORE_INJECTION,
} from "@/constants";

import { shouldInvalidateCache } from "@/utils/version.js";
import { useProfileStore } from "./profile.js";

export const usePulseStore = defineStore(
  "pulse",
  () => {
    const pulseFeed = ref([]);
    const pendingUpdateCards = ref([]);
    const pulseOnlineUsers = ref([]);
    const pulseLoading = ref(false);
    const onlineUsersLoading = ref(false);
    const hasMorePulseEntries = ref(false);
    const hasMoreOnlineUsers = ref(false);
    const onlineUsersPage = ref(1);

    const sessionCursor = ref(null);
    const sessionStartTime = ref(null);
    const pulseEntriesSinceLastInjection = ref(0);

    const scrollPosition = ref(0);
    const isRestoringScroll = ref(false);
    const cacheTimestamp = ref(null);
    const cacheAppVersion = ref(null);
    const hasNewPulseEntries = ref(false);

    const profileStore = useProfileStore();

    // Helper method to get only pulse entries from feed (excluding updates)
    const getPulseEntriesFromFeed = () => {
      return pulseFeed.value
        .filter((feedItem) => feedItem.type === "entry")
        .map((feedItem) => feedItem.item);
    };

    // Helper method to find a pulse entry in the feed
    const findPulseEntryInFeed = (entryId) => {
      return pulseFeed.value.find(
        (feedItem) => feedItem.type === "entry" && feedItem.item.id === entryId
      );
    };

    const resetPulsePostsState = () => {
      sessionCursor.value = null;
      sessionStartTime.value = null;
      pulseFeed.value = [];
      pendingUpdateCards.value = [];
      pulseEntriesSinceLastInjection.value = 0;
      hasMorePulseEntries.value = false;
      scrollPosition.value = 0;
      isRestoringScroll.value = false;
      hasNewPulseEntries.value = false;
    };

    const updateSessionState = (cursor, startTime = null) => {
      sessionCursor.value = cursor;
      if (startTime) {
        sessionStartTime.value = startTime;
      }
    };

    const setScrollPosition = (position) => {
      scrollPosition.value = position;
    };

    const setIsRestoringScroll = (restoring) => {
      isRestoringScroll.value = restoring;
    };

    const setCacheTimestamp = () => {
      cacheTimestamp.value = Date.now();
      cacheAppVersion.value = APP_VERSION;
    };

    const isCacheValid = () => {
      if (!cacheTimestamp.value) {
        return false;
      }

      // Invalidate cache if it was created with an older version
      if (shouldInvalidateCache(APP_VERSION, cacheAppVersion.value)) {
        return false;
      }

      const cacheAge = Date.now() - cacheTimestamp.value;
      return cacheAge <= PULSE_CACHE_DURATION;
    };

    const clearCache = () => {
      pulseFeed.value = [];
      pendingUpdateCards.value = [];
      pulseOnlineUsers.value = [];
      sessionCursor.value = null;
      sessionStartTime.value = null;
      pulseEntriesSinceLastInjection.value = 0;
      hasMorePulseEntries.value = false;
      scrollPosition.value = 0;
      cacheTimestamp.value = null;
      cacheAppVersion.value = null;
      onlineUsersLoading.value = false;
      hasMoreOnlineUsers.value = false;
      onlineUsersPage.value = 1;
    };

    const resetOnlineUsersState = () => {
      pulseOnlineUsers.value = [];
      onlineUsersPage.value = 1;
      hasMoreOnlineUsers.value = false;
      onlineUsersLoading.value = false;
    };

    const hasValidCache = () => {
      return isCacheValid() && pulseFeed.value.length > 0;
    };

    const refreshPendingUpdates = () => {
      const pendingUpdates = profileStore.getPendingUpdates();
      pendingUpdateCards.value = pendingUpdates;
    };

    const getNextAvailableUpdateCard = () => {
      if (pendingUpdateCards.value.length === 0) {
        return null;
      }

      // Get the first available card and remove it from the array
      return pendingUpdateCards.value.shift();
    };

    const getRandomInjectionThreshold = () => {
      return (
        Math.floor(
          Math.random() *
            (MAX_PULSE_ENTRIES_BEFORE_INJECTION -
              MIN_PULSE_ENTRIES_BEFORE_INJECTION +
              1)
        ) + MIN_PULSE_ENTRIES_BEFORE_INJECTION
      );
    };

    const appendToPulseFeed = (entries, shouldCheckInjection = false) => {
      // Refresh pending updates if array is empty
      if (shouldCheckInjection && pendingUpdateCards.value.length === 0) {
        refreshPendingUpdates();
      }

      // Convert entries to feed format
      const feedEntries = entries.map((entry) => ({
        item: entry,
        type: "entry",
      }));

      // Check if we should inject an update card
      if (
        shouldCheckInjection &&
        pulseEntriesSinceLastInjection.value >= getRandomInjectionThreshold() &&
        pendingUpdateCards.value.length > 0
      ) {
        // Calculate random position in the last 25% of incoming entries
        const minPosition = Math.floor(entries.length * 0.75);
        const maxPosition = entries.length - 1;
        const randomPosition =
          Math.floor(Math.random() * (maxPosition - minPosition + 1)) +
          minPosition;

        // Get and mark update card
        const updateCard = getNextAvailableUpdateCard();
        if (updateCard) {
          profileStore.updateCardSeen(updateCard.id);

          // Insert update card at random position
          feedEntries.splice(randomPosition, 0, {
            item: updateCard,
            type: "update",
          });

          pulseEntriesSinceLastInjection.value = 0;
        }
      }

      pulseFeed.value = [...pulseFeed.value, ...feedEntries];
      pulseEntriesSinceLastInjection.value += entries.length;
    };

    const setPulseFeed = (entries) => {
      // Convert entries to feed format
      const feedEntries = entries.map((entry) => ({
        item: entry,
        type: "entry",
      }));

      pulseFeed.value = feedEntries;
      pulseEntriesSinceLastInjection.value = entries.length;
    };

    const removeUpdateCardFromFeed = (cardId) => {
      pulseFeed.value = pulseFeed.value.filter((feedItem) => {
        if (feedItem.type === "update" && feedItem.item.id === cardId) {
          return false;
        }
        return true;
      });
    };

    const fetchPulseEntries = async (
      opts = {
        additional: false,
        forceRefresh: false,
      }
    ) => {
      try {
        // Skip if entries exist and not forcing refresh or pagination
        if (
          getPulseEntriesFromFeed().length > 0 &&
          !opts.additional &&
          !opts.forceRefresh
        ) {
          return;
        }

        // Reset session state on fresh load
        if (!opts.additional || opts.forceRefresh) {
          resetPulsePostsState();
        }

        pulseLoading.value = true;

        const requestParams = {};

        // Add cursor for pagination
        if (opts.additional && sessionCursor.value) {
          requestParams.cursor = sessionCursor.value;
        }

        const result = await fetchPulseReq(requestParams);

        const { users, pagination, session } = result;

        // Update session state
        if (pagination) {
          updateSessionState(pagination.cursor, session?.start_time);
          hasMorePulseEntries.value = pagination.has_more;
        }

        // Update entries
        if (opts.additional) {
          appendToPulseFeed(users, true); // Enable injection check for additional mode
        } else {
          setPulseFeed(users);
        }
      } catch (ex) {
        console.error("Error fetching pulse entries:", ex);
        if (!opts.additional) {
          pulseOnlineUsers.value = [];
          hasMoreOnlineUsers.value = false;
        }
      } finally {
        pulseLoading.value = false;
      }
    };

    const fetchHasNewPulseEntries = async () => {
      try {
        // Need session start time to check for new entries
        if (!sessionStartTime.value) return false;

        const result = await fetchPulseNewReq({
          feed_session_start_time: sessionStartTime.value,
        });

        return result.success ? result.data.hasNew : result.hasNew;
      } catch (ex) {
        console.error("Error checking for new pulse entries:", ex);
        return false;
      }
    };

    const fetchOnlineProfiles = async (opts = { additional: false }) => {
      try {
        onlineUsersLoading.value = true;

        const result = await onlineProfilesReq(onlineUsersPage.value);

        if (result.users && result.users.length > 0) {
          if (opts.additional) {
            const existingIds = new Set(
              pulseOnlineUsers.value.map((user) => user.id)
            );
            const newUsers = result.users.filter(
              (user) => !existingIds.has(user.id)
            );
            pulseOnlineUsers.value = [...pulseOnlineUsers.value, ...newUsers];
          } else {
            pulseOnlineUsers.value = result.users;
          }
          onlineUsersPage.value++;
        } else if (!opts.additional) {
          pulseOnlineUsers.value = [];
        }

        hasMoreOnlineUsers.value = result.hasMore || false;
      } catch (ex) {
        console.error("Error fetching online profiles:", ex);
        if (!opts.additional) {
          pulseOnlineUsers.value = [];
          hasMoreOnlineUsers.value = false;
        }
      } finally {
        onlineUsersLoading.value = false;
      }
    };

    const removePulseEntriesFromUser = (userId) => {
      pulseFeed.value = pulseFeed.value.filter((feedItem) => {
        if (feedItem.type === "update") return true;
        return feedItem.item.user.id !== userId;
      });
    };

    const markPulseEntryUserLiked = (pulseId) => {
      const feedEntryIndex = pulseFeed.value.findIndex(
        (feedItem) => feedItem.type === "entry" && feedItem.item.id === pulseId
      );
      if (feedEntryIndex !== -1) {
        const currentEntry = pulseFeed.value[feedEntryIndex].item;
        const currentTotalLikes = currentEntry.likes_count || 0;
        const updatedEntry = {
          ...currentEntry,
          liked: true,
          likes_count: currentTotalLikes + 1,
        };
        pulseFeed.value[feedEntryIndex].item = updatedEntry;
      }
    };

    const markPulseEntryUserUnliked = (pulseId) => {
      const feedEntryIndex = pulseFeed.value.findIndex(
        (feedItem) => feedItem.type === "entry" && feedItem.item.id === pulseId
      );
      if (feedEntryIndex !== -1) {
        const currentEntry = pulseFeed.value[feedEntryIndex].item;
        const currentTotalLikes = currentEntry.likes_count || 0;
        const updatedEntry = {
          ...currentEntry,
          liked: false,
          likes_count: Math.max(0, currentTotalLikes - 1), // Ensure count doesn't go below 0
        };
        pulseFeed.value[feedEntryIndex].item = updatedEntry;
      }
    };

    const addPulseEntryUserDialogId = (userId, dialogId) => {
      pulseFeed.value = pulseFeed.value.map((feedItem) => {
        if (feedItem.type !== "entry" || feedItem.item.user.id !== userId)
          return feedItem;
        return {
          ...feedItem,
          item: {
            ...feedItem.item,
            user: {
              ...feedItem.item.user,
              dialog_id: dialogId,
            },
          },
        };
      });
    };

    // Watch for intentional profile setting
    watch(
      () => profileStore.settingsUpdatedAt,
      (newTimestamp) => {
        if (newTimestamp) {
          // Reset both online users and pulse posts when settings are intentionally updated
          resetOnlineUsersState();
          resetPulsePostsState();
        }
      }
    );

    return {
      pulseLoading,
      pulseFeed,
      pendingUpdateCards,
      pulseOnlineUsers,
      onlineUsersLoading,
      hasMoreOnlineUsers,
      onlineUsersPage,
      hasMorePulseEntries,
      sessionCursor,
      sessionStartTime,
      pulseEntriesSinceLastInjection,
      scrollPosition,
      isRestoringScroll,
      hasNewPulseEntries,
      fetchPulseEntries,
      fetchHasNewPulseEntries,
      fetchOnlineProfiles,
      removePulseEntriesFromUser,
      markPulseEntryUserLiked,
      markPulseEntryUserUnliked,
      addPulseEntryUserDialogId,
      removeUpdateCardFromFeed,
      refreshPendingUpdates,
      resetPulsePostsState,
      setScrollPosition,
      setIsRestoringScroll,
      setCacheTimestamp,
      isCacheValid,
      clearCache,
      hasValidCache,
      cacheTimestamp,
      cacheAppVersion,
      resetOnlineUsersState,
      getPulseEntriesFromFeed,
      findPulseEntryInFeed,
    };
  },
  {
    persist: {
      paths: [
        "pulseFeed",
        "pulseEntriesSinceLastInjection",
        "sessionSeed",
        "sessionCursor",
        "sessionStartTime",
        "hasMorePulseEntries",
        "scrollPosition",
        "cacheTimestamp",
        "cacheAppVersion",
      ],
    },
  }
);
